/** Contracts for the phase 1 screens: import exceptions, nominal mapping, adjusted TB and journals. */

export interface Pagination {
  totalItemCount: number;
  pageNumber: number;
  pageSize: number;
  isLastPage: boolean;
}

/* ---------------------------------------------------------------- Import exceptions */

/** 0 None, 1 Map to nominal, 2 Correct amount, 3 Ignore, 4 Add the account to the client's chart (APT334). */
export type ImportExceptionAction = 0 | 1 | 2 | 3 | 4;

/** 0 Open, 1 Resolved, 2 Ignored. */
export type ImportExceptionStatus = 0 | 1 | 2;

export interface ImportSummaryRow {
  importBatchId: string;
  sourceType: string;
  importedAtUtc: string;
  importedBy: string;
  lineCount: number;
  totalDebit: number;
  totalCredit: number;
  status: number;
  /** APT334: lines of a live (committed) batch still held back - an account the chart lacks, a reserved code. The rest of it is posted. */
  heldLineCount?: number;
}

export interface ImportExceptionRow {
  importExceptionId: string;
  importBatchId: string;
  importStagingLineId: number | null;
  rowNumber: number;
  code: string | null;
  description: string | null;
  debit: number;
  credit: number;
  errorCode: string;
  errorMessage: string;
  severity: string;
  action: ImportExceptionAction;
  status: ImportExceptionStatus;
  resolvedReportingCodeId: string | null;
  resolvedReportingCode: string | null;
  resolvedCode: string | null;
  resolutionNote: string | null;
  resolvedAtUtc: string | null;
  raisedAtUtc: string;
  eTag: string;
}

export interface ImportExceptionSummary {
  totalCount: number;
  openCount: number;
  resolvedCount: number;
  ignoredCount: number;
  blockingCount: number;
  lastValidatedAtUtc: string | null;
  /** APT334: the lines of the live (committed) batch still held back, and what they come to: the adjusted trial balance is out of balance
   * by heldDebit - heldCredit until they are mapped, added to the chart or corrected. */
  heldLineCount?: number;
  heldDebit?: number;
  heldCredit?: number;
}

export interface ImportExceptionPage {
  accountingPeriodId: string;
  items: ImportExceptionRow[];
  summary: ImportExceptionSummary;
  pagination: Pagination;
}

export interface ImportExceptionQuery {
  importBatchId?: string | null;
  status?: ImportExceptionStatus | null;
  search?: string | null;
  pageNumber: number;
  pageSize: number;
}

export interface ResolveImportExceptionPayload {
  action: ImportExceptionAction;
  nominalCode?: string | null;
  description?: string | null;
  reportingCodeId?: string | null;
  debit?: number | null;
  credit?: number | null;
  note?: string | null;
  expectedETag?: string | null;
  /** APT334: the account type of the chart account Add to chart creates (its name is `description`). */
  accountTypeId?: number | null;
}

export interface ResolveImportExceptionResponse {
  importExceptionId: string;
  importBatchId: string;
  status: ImportExceptionStatus;
  openExceptionCount: number;
  /** APT334: lines posted into a partly posted batch by this resolution, and how many it still holds. */
  linesPosted?: number;
  heldLinesRemaining?: number;
}

export interface ImportValidationResult {
  importBatchId: string;
  openExceptionCount: number;
  lineCount: number;
  totalDebit: number;
  totalCredit: number;
  difference: number;
  readyToCommit: boolean;
}

export interface ResumeImportResponse {
  importBatchId: string;
  committed: boolean;
  openExceptionCount: number;
  lineCount: number;
  linesCommitted: number;
  nominalsCreated: number;
  mappingsApplied: number;
  balanceVersion: number;
  committedAtUtc: string | null;
  /** APT334: why the batch was not committed - or, when it was, what it left held. */
  message?: string | null;
  /** APT334: lines left staged because their account is not in the chart (or is reserved); the rest was committed. */
  heldLines?: number;
}

/* ---------------------------------------------------------------- Mapping */

/** 0 Not reviewed, 1 Reviewed, 2 Queried. */
export type ReviewStatus = 0 | 1 | 2;

/** 0 P&L, 1 Balance sheet, 2 Equity, 3 Cash flow, 4 Notes, 5 Memorandum. */
export type StatementArea = 0 | 1 | 2 | 3 | 4 | 5;

export interface ReportingCodeNode {
  reportingCodeId: string;
  code: string;
  parentCode: string | null;
  description: string;
  statementArea: StatementArea;
  normalSign: number;
  sortOrder: number;
  isPostable: boolean;
  children: ReportingCodeNode[];
}

export interface MappingWorkspaceRow {
  nominalAccountId: string;
  nominalCode: string;
  nominalDescription: string;
  displayDescription: string | null;
  reportingCodeId: string | null;
  reportingCode: string | null;
  reportingCodeDescription: string | null;
  statementArea: StatementArea | null;
  mappingSource: number | null;
  reviewedStatus: ReviewStatus;
  sourceAmount: number;
  journalAmount: number;
  adjustedAmount: number;
  priorAmount: number;
  priorYearAmount: number;
  suggestedReportingCodeId: string | null;
  suggestedReportingCode: string | null;
  isMaterial: boolean;
  updatedAtUtc: string | null;
  eTag: string;
  /** APT349: the nominal's natural side from its category (account type; 1 debit, -1 credit), else its mapped code's; null/absent when neither. The amounts stay stored (debit positive). */
  normalSign?: number | null;
  /** APT335: the account type of the client's chart account on this nominal's code (null/absent for a nominal the chart does not hold). */
  accountTypeId?: number | null;
  accountTypeName?: string | null;
  /** APT335: the account is one of the standard chart: its mapping is controlled (the type's default code is `systemDefaultCode`). */
  isSystemMapping?: boolean;
  systemDefaultCode?: string | null;
  /** APT335: nobody may change the mapping (a system mapping; the client's answer of 28 Oct was "Nobody"); `lockReason` says why. */
  isLocked?: boolean;
  lockReason?: string | null;
  /** APT335: the reporting codes on offer for the account (its section and side); null/absent = every code. */
  allowedReportingCodes?: string[] | null;
  /** APT335: the saved code is not among the codes on offer (an older choice) - still shown, flagged. */
  mappedOutsideAllowed?: boolean;
}

export interface MappingWorkspaceSummary {
  totalCount: number;
  regulatoryPackVersionId: string;
  totalNominals: number;
  unmappedCount: number;
  unmappedMaterialCount: number;
  reviewedCount: number;
  materialityThreshold: number;
  unmappedAmount: number;
}

export interface MappingWorkspacePage {
  accountingPeriodId: string;
  items: MappingWorkspaceRow[];
  summary: MappingWorkspaceSummary;
  pagination: Pagination;
}

export interface MappingWorkspaceQuery {
  unmappedOnly: boolean;
  materialOnly: boolean;
  reviewedStatus?: ReviewStatus | null;
  statementArea?: StatementArea | null;
  search?: string | null;
  sortBy?: string | null;
  sortDescending: boolean;
  pageNumber: number;
  pageSize: number;
}

export interface MappingOverrideInput {
  nominalAccountId: string;
  reportingCodeId: string | null;
  mappingSource: number;
  reviewedStatus: ReviewStatus;
  displayDescription?: string | null;
  expectedETag?: string | null;
}

export interface SaveMappingsResponse {
  applied: number;
  cleared: number;
  reviewPointsInvalidated: number;
  savedAtUtc: string;
  balanceVersion: number;
  /** APT335: the warnings the user confirmed with this save. */
  warningsConfirmed?: number;
  /** APT335: Apply Suggested only - suggestions left for the user because the code does not fit the account. */
  skipped?: number;
}

/** APT335: what a save adds to the mappings: the user confirmed the warnings. */
export interface SaveMappingsOptions {
  confirmWarnings?: boolean;
}

/** APT335: one finding of the mapping check (ChartMapping/consistency): Severity Error or Warning. */
export interface MappingCheckIssue {
  severity: string;
  code: string;
  message: string;
  businessType?: number | null;
  accountCode?: string | null;
  accountTypeId?: number | null;
}

export interface MappingCheckReport {
  accountingPeriodId: string;
  checked: number;
  issues: MappingCheckIssue[];
}

export interface MappingSuggestion {
  nominalAccountId: string;
  nominalCode: string;
  nominalDescription: string;
  suggestedReportingCodeId: string;
  suggestedReportingCode: string;
  suggestedReportingCodeDescription: string | null;
  basis: string;
  adjustedAmount: number;
}

/* ---------------------------------------------------------------- Adjusted trial balance */

export interface VariancePeriodColumn {
  periodIndex: number;
  accountingPeriodId: string;
  startDate: string;
  endDate: string;
  label: string;
  isCurrent: boolean;
}

export interface VarianceLine {
  reportingCodeId: string;
  reportingCode: string;
  description: string;
  statementArea: StatementArea;
  parentCode: string | null;
  sortOrder: number;
  sourceAmount: number;
  journalAmount: number;
  adjustedAmount: number;
  priorAmount: number;
  varianceAmount: number;
  variancePercent: number | null;
  yearAmounts: number[];
  /** APT349: the reporting code's natural side (1 debit, -1 credit). The amounts stay stored (debit positive); screens show them with `formatNatural`. */
  normalSign: number;
}

export interface VarianceTotals {
  totalCount: number;
  totalCurrent: number;
  totalPrior: number;
  totalVariance: number;
  lastRebuiltAtUtc: string | null;
}

export interface TrialBalanceVarianceResponse {
  accountingPeriodId: string;
  periods: VariancePeriodColumn[];
  lines: VarianceLine[];
  totals: VarianceTotals;
}

export interface TrialBalanceVarianceQuery {
  years: number;
  statementArea?: StatementArea | null;
  search?: string | null;
  includeZeroBalances: boolean;
}

export interface AtbDrilldownNominal {
  nominalAccountId: string;
  code: string;
  description: string;
  displayDescription: string | null;
  sourceAmount: number;
  journalAmount: number;
  adjustedAmount: number;
  priorAmount: number;
  /**
   * APT349 follow-up: the nominal's OWN natural side from its category (account type: 1 debit, -1 credit), so accumulated depreciation inside an asset code
   * shows positive. Absent from an API that predates it: the clicked code's side is used.
   */
  normalSign?: number | null;
}

export interface AtbDrilldownJournalLine {
  journalId: string;
  journalNo: number;
  typeCode: string;
  journalDate: string;
  narrative: string | null;
  status: number;
  /** Journal status 0 Draft, 1 Posted, 2 Reversed, 3 Reversal; may be absent on older API builds. */
  journalStatus?: number | null;
  /** The journal number this journal reverses. */
  reversalOfJournalNo?: number | null;
  /** The journal number that reversed this journal. */
  reversedByJournalNo?: number | null;
  /** APT336: the other period's dates when the journal it reverses is in ANOTHER period (a reversal posted in the next period). */
  reversalOfPeriodStart?: string | null;
  reversalOfPeriodEnd?: string | null;
  /** APT336: the other period's dates when the journal that reversed this one is in ANOTHER period (the next one). */
  reversedByPeriodStart?: string | null;
  reversedByPeriodEnd?: string | null;
  journalLineId: string;
  lineNo: number;
  debit: number;
  credit: number;
  lineNarrative: string | null;
  nominalCode: string | null;
  nominalDescription: string | null;
}

export interface AtbDrilldown {
  reportingCodeId: string;
  nominals: AtbDrilldownNominal[];
  journalLines: AtbDrilldownJournalLine[];
}

/* ---------------------------------------------------------------- Journals */

/** 0 Draft, 1 Posted, 2 Reversed, 3 Reversal. */
export type JournalStatus = 0 | 1 | 2 | 3;

export interface JournalTypeOption {
  typeCode: string;
  name: string;
  description: string | null;
  isGroupOnly: boolean;
  isElimination: boolean;
  sortOrder: number;
}

export interface JournalScreenRow {
  journalId: string;
  journalNo: number;
  typeCode: string;
  typeName: string;
  journalDate: string;
  narrative: string | null;
  status: JournalStatus;
  reviewStatus: ReviewStatus;
  reviewComment: string | null;
  isGroupOnly: boolean;
  isElimination: boolean;
  totalDebit: number;
  totalCredit: number;
  lineCount: number;
  createdAtUtc: string;
  postedAtUtc: string | null;
  reversalOfJournalId: string | null;
  reversedByJournalId: string | null;
  /** Journal numbers of the linked reversal; may be absent on older API builds. */
  reversalOfJournalNo?: number | null;
  reversedByJournalNo?: number | null;
  /** APT336: the other period's dates when the linked journal is in ANOTHER period (a reversal posted in the next period). */
  reversalOfPeriodStart?: string | null;
  reversalOfPeriodEnd?: string | null;
  reversedByPeriodStart?: string | null;
  reversedByPeriodEnd?: string | null;
  eTag: string;
}

/** APT336: the next accounting period a journal of the period can be reversed into (the first period of the entity that starts after it ends). */
export interface NextReversalPeriod {
  accountingPeriodId: string;
  startDate: string;
  endDate: string;
  /** False when the next period cannot take postings (finalised or locked); `unavailableReason` says why. */
  available: boolean;
  unavailableReason: string | null;
}

/** APT336: where a posted journal of a period can be reversed: always in the same period, and in `next` when there is one. */
export interface JournalReversalOptions {
  accountingPeriodId: string;
  startDate: string;
  endDate: string;
  next: NextReversalPeriod | null;
  /** Set when there is no next period; it is never created silently. */
  noNextPeriodMessage: string | null;
  /**
   * APT336 (the client's answer for a FINALISED period): the journal's own period is finalised (Finalised or Filed). A reversal within it is refused
   * (`samePeriodAllowed` false, `samePeriodMessage` says to remove or alter the finalisation first); one into the next period needs the user's
   * approval of `confirmationMessage` and is dated `forcedReversalDate`, the next period's first day. Absent on an older API: read as not finalised.
   */
  finalised?: boolean;
  samePeriodAllowed?: boolean;
  samePeriodMessage?: string | null;
  confirmationRequired?: boolean;
  forcedReversalDate?: string | null;
  confirmationMessage?: string | null;
}

export interface JournalScreenSummary {
  totalCount: number;
  draftCount: number;
  postedCount: number;
  reversedCount: number;
  unreviewedCount: number;
  netAdjustment: number;
  priorPeriodAdjustment: number;
}

export interface JournalScreenPage {
  accountingPeriodId: string;
  items: JournalScreenRow[];
  summary: JournalScreenSummary;
  pagination: Pagination;
}

export interface JournalScreenQuery {
  status?: JournalStatus | null;
  typeCode?: string | null;
  reviewStatus?: ReviewStatus | null;
  groupOnly?: boolean | null;
  search?: string | null;
  pageNumber: number;
  pageSize: number;
}

export interface JournalLineInput {
  nominalAccountId?: string | null;
  reportingCodeId?: string | null;
  debit: number;
  credit: number;
  narrative?: string | null;
}

export interface JournalLineDetail {
  journalLineId: string;
  nominalAccountId: string | null;
  nominalCode: string | null;
  reportingCodeId: string | null;
  reportingCode: string | null;
  debit: number;
  credit: number;
  narrative: string | null;
  dimensionJson: string | null;
  lineNo: number;
}

export interface JournalDetail {
  journalId: string;
  accountingPeriodId: string;
  journalNo: number;
  typeCode: string;
  journalDate: string;
  narrative: string | null;
  status: JournalStatus;
  createdBy: string;
  createdAtUtc: string;
  postedAtUtc: string | null;
  reversalOfJournalId: string | null;
  reversedByJournalId: string | null;
  totalDebit: number;
  totalCredit: number;
  lines: JournalLineDetail[];
  eTag: string;
}

export interface CreateJournalPayload {
  typeCode: string;
  journalDate: string;
  narrative?: string | null;
  lines: JournalLineInput[];
}

export interface SaveJournalPayload {
  journalDate: string;
  narrative?: string | null;
  lines: JournalLineInput[];
}

export interface JournalNominalOption {
  nominalAccountId: string;
  code: string;
  description: string;
  displayDescription: string | null;
  reportingCode: string | null;
  adjustedAmount: number;
  priorYearAmount: number;
  /** APT349: the nominal's natural side from its category (1 debit, -1 credit), else its mapped code's; null/absent when neither. */
  normalSign?: number | null;
}

export interface SetJournalReviewPayload {
  reviewStatus: ReviewStatus;
  comment?: string | null;
  expectedETag?: string | null;
}

/** format: 1 CSV, 2 Excel, 3 PDF. */
export interface ExportOptions {
  format: number;
  isPrint: boolean;
}

// ap5-a6: adding a chart account to a period so journals can use a nominal that the committed
// trial balance import never contained (item 127/129).
export interface EnsureNominalFromChartPayload {
  chartAccountId?: string | null;
  accountCode?: string | null;
}

export interface EnsureNominalFromChartResponse {
  nominalAccountId: string;
  code: string;
  created: boolean;
  message: string;
}

/** What `pickDefaultPeriodId` may know beyond the period list itself. */
export interface DefaultPeriodHints {
  /** The period the user last chose for this client in this session. */
  rememberedId?: string | null;
  /** Ids of the periods that have a trial balance with lines (from the client's trial balance list). */
  idsWithData?: ReadonlySet<string> | null;
  /** Today as `yyyy-MM-dd`; defaults to the local date. */
  today?: string;
}

const dayOf = (value: string | null | undefined): string => `${value ?? ''}`.slice(0, 10);

function localToday(): string {
  const now = new Date();
  const pad = (n: number) => `${n}`.padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The period a period-scoped screen opens on. ap5-a6 (item 126) found that defaulting to `periods[0]` (the newest period,
 * which is generated ahead of time and has no data) left Nominal Mapping, Approval, the Accounts Builder and the rest looking
 * empty or broken; the first fix (the newest period with a status above Draft) still chose a future period for a client whose
 * data was imported without the status moving. In order:
 *
 *  1. the period the user last selected for this client in this session (`rememberedId`);
 *  2. the latest period that has a trial balance with lines (`idsWithData`);
 *  3. the latest period whose end date is on or before today (a comparative period only if nothing else qualifies);
 *  4. the most recent period.
 *
 * "Latest" is by period end date, not by the order the API returned.
 */
export function pickDefaultPeriodId<T extends { id: string; periodTo?: string | null; isComparative?: boolean }>(
  periods: readonly T[] | null | undefined,
  hints: DefaultPeriodHints = {},
): string | null {
  if (!periods || periods.length === 0) {
    return null;
  }
  const remembered = hints.rememberedId ? periods.find((p) => p.id === hints.rememberedId) : undefined;
  if (remembered) {
    return remembered.id;
  }
  const latestFirst = [...periods].sort((a, b) => dayOf(b.periodTo).localeCompare(dayOf(a.periodTo)));
  const withData = hints.idsWithData
    ? (latestFirst.find((p) => hints.idsWithData!.has(p.id) && !p.isComparative) ??
      latestFirst.find((p) => hints.idsWithData!.has(p.id)))
    : undefined;
  if (withData) {
    return withData.id;
  }
  const today = hints.today ?? localToday();
  const ended = (p: T) => dayOf(p.periodTo) !== '' && dayOf(p.periodTo) <= today;
  const endedPeriod = latestFirst.find((p) => ended(p) && !p.isComparative) ?? latestFirst.find(ended);
  if (endedPeriod) {
    return endedPeriod.id;
  }
  return (latestFirst.find((p) => !p.isComparative) ?? latestFirst[0]).id;
}
